/**
 * Losses in the pipeline (04.1 E2-E5, 04.2 E4-E6; ADR-0057): for each loan book, how far its driver has fallen at worst, the loss already recognised and still waiting to be charged off
 * (the backlog, reserved in full), how much of it the book works out in the next year, and the loans behind it on nonaccrual. Read from the bank's own `BookCreditState` and the
 * book's credit model; a book with nothing in the pipeline is left out. Pure.
 */
import type { BookCreditState, BookId, ProductDef } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { formatUsdM } from "../format";
import { BOOK_LABELS } from "./statements";

/** What each driver is called and how its level reads: a fall in an index as a share, a rise in unemployment in points. */
const DRIVERS = {
  unemployment: { name: "Unemployment", verb: "rose", points: true }, hpi: { name: "House prices", verb: "fell", points: false },
  creIndex: { name: "Commercial property prices", verb: "fell", points: false }, officeIndex: { name: "Office prices", verb: "fell", points: false }, gdp: { name: "GDP", verb: "fell", points: false },
} as const;
type DriverKey = keyof typeof DRIVERS;

export interface PipelineRow {
  readonly book: BookId; readonly label: string;
  /** "Commercial property prices fell 7.0%"; "None yet" while the driver has not fallen. */
  readonly worst: string;
  readonly backlogUsd: number;
  /** Share of the backlog the book charges off in the next twelve months at its monthly rate. */
  readonly nextYearRate: number | null;
  readonly nonaccrualUsd: number;
}

/** The share charged off over `months` at `monthlyRate` of the remaining backlog each month. */
export const workedOffRate = (monthlyRate: number, months = 12): number => 1 - (1 - monthlyRate) ** months;

export function pipelineRows(credit: Partial<Record<BookId, BookCreditState>>, products: readonly ProductDef[]): readonly PipelineRow[] {
  return (Object.keys(credit) as BookId[]).flatMap((book): PipelineRow[] => {
    const state = credit[book];
    if (state === undefined) return [];
    const [worst, backlogUsd, nonaccrualUsd] = [state.worstDriverLevel ?? 0, state.workoutBacklogUsd ?? 0, state.stressNonaccrualUsd ?? 0];
    if (worst === 0 && backlogUsd === 0 && nonaccrualUsd === 0) return [];
    const model = products.filter((p) => p.bookId === book).flatMap((p) => p.models).find((m) => m.kind === "creditPd");
    const params = model?.kind === "creditPd" ? model.params : undefined;
    const driver = (Object.keys(params?.macroBetas ?? {}) as DriverKey[]).find((k) => DRIVERS[k] !== undefined);
    const info = driver === undefined ? undefined : DRIVERS[driver];
    const level = worst === 0 ? "None yet" : info === undefined ? `Worst move in its driver: ${formatMetric(worst, "rate", { precision: 1 })}`
      : `${info.name} ${info.verb} ${info.points ? `${formatMetric(worst * 100, "index", { precision: 1 })} points` : formatMetric(worst, "rate", { precision: 1 })}`;
    return [{ book, label: BOOK_LABELS[book] ?? book, worst: level, backlogUsd, nextYearRate: params?.lossCurve === undefined ? null : workedOffRate(params.lossCurve.workoutRatePerMonth), nonaccrualUsd }];
  });
}

/** "$38.0M to charge off, about 46% of it in the next year". */
export const backlogText = (row: PipelineRow): string =>
  `${formatUsdM(row.backlogUsd)}${row.nextYearRate === null || row.backlogUsd === 0 ? "" : `, about ${formatMetric(row.nextYearRate, "rate", { precision: 0 })} of it charged off in the next year`}`;
